import { useEffect, useRef, useState } from "react";
import {
  AlertTriangle,
  Check,
  ChevronDown,
  ChevronRight,
  Download,
  FileSpreadsheet,
  Loader2,
  Minus,
  Plus,
  Trash2,
  UploadCloud,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { DayField } from "@/components/DayField";
import { api, apiError, downloadFile, fmtWhen } from "@/lib/api";
import { rupees } from "@/pages/InvoiceTracker";
import { TemplateDownload } from "@/components/TemplateDownload";
import { ComplianceTemplateDownload } from "@/components/ComplianceTemplateDownload";
import { MissingDataPanel } from "@/components/MissingDataPanel";
import { TemplateDiff } from "@/components/TemplateDiff";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { UploadBlockers } from "@/components/UploadBlockers";
import { FlushAll } from "@/components/FlushAll";
import { FlushUnit } from "@/components/FlushUnit";
import { useAuth } from "@/context/AuthContext";

const Count = ({ label, value, tone }) => (
  <div className="bg-card p-3">
    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
    <div className="font-data mt-1 text-xl font-bold" style={tone ? { color: tone } : undefined}>
      {value}
    </div>
  </div>
);

const Row = ({ children }) => (
  <li className="border-b border-border px-3 py-2 text-xs last:border-b-0">{children}</li>
);

const Fold = ({ title, count, children, testId }) => {
  const [open, setOpen] = useState(false);
  if (!count) return null;
  return (
    <div className="border border-border">
      <button
        type="button"
        data-testid={testId}
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between px-3 py-2 text-left text-xs font-semibold transition-colors duration-200 hover:bg-muted"
      >
        <span>
          {title} ({count})
        </span>
        {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
      </button>
      {open && <ul className="max-h-64 overflow-y-auto border-t border-border">{children}</ul>}
    </div>
  );
};

export default function DataFiles() {
  const { isAdmin, scopedPlant, isPartner, isPartnerManager, canUpload, shows,
    writable } = useAuth();
  const [library, setLibrary] = useState(null);
  const [staged, setStaged] = useState(null);
  const [picked, setPicked] = useState([]);
  const [force, setForce] = useState(false);
  const [dropMissing, setDropMissing] = useState(true);
  const [replaceAll, setReplaceAll] = useState(false);
  const [asOn, setAsOn] = useState("");
  const [busy, setBusy] = useState(false);
  const [applying, setApplying] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [result, setResult] = useState(null);
  const [blockers, setBlockers] = useState(null);
  const [refusal, setRefusal] = useState("");
  const [askApply, setAskApply] = useState(false);
  const fileRef = useRef();

  const load = () => api.get("/registers/library").then((r) => setLibrary(r.data));

  useEffect(() => {
    load();
  }, []);

  const send = async (file) => {
    if (!file) return;
    setBusy(true);
    setResult(null);
    setBlockers(null);
    setRefusal("");
    try {
      const body = new FormData();
      body.append("file", file);
      body.append("as_on", asOn.trim());
      const { data } = await api.post("/registers/uploads", body, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setStaged(data);
      setPicked((data.units || []).map((u) => u.unit));
      // A unit's first fill is a complete replacement — ticked for the uploader by default.
      setReplaceAll((data.first_fill_units || []).length > 0);
      setForce(false);
      toast.success(
        data.kind === "coursera"
          ? "Coursera tracker read. Check the difference below."
          : data.kind === "template"
            ? `Bulk update workbook read — ${data.template.total_rows} row(s). Nothing is saved yet.`
            : data.kind === "compliance"
            ? `Compliance workbook read — ${data.compliance.use_cases.length} usecase(s), ${data.compliance.readings_new} new day(s)${data.compliance.readings_removed ? `, ${data.compliance.readings_removed} to be removed` : ""}. Nothing is saved yet.`
            : data.kind === "invoices"
            ? `Payments tracker read — ${data.invoices.rows} invoice(s) across ${data.invoices.entities.length} entity/entities. Nothing is saved yet.`
            : data.kind === "team"
              ? `Team list read — ${data.team.total} member(s). Nothing is saved yet.`
              : `Read ${data.units.length} unit sheet(s). Nothing is saved yet.`,
      );
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.blockers?.length) setBlockers(detail);
      else setRefusal(apiError(err));
      toast.error(apiError(err));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const apply = async () => {
    setAskApply(false);
    setApplying(true);
    try {
      const { data } = await api.post(`/registers/uploads/${staged.id}/confirm`, {
        units: picked,
        force,
        drop_missing: dropMissing,
        replace_all: replaceAll,
      });
      setResult(data);
      if (data.awaiting_units?.length)
        toast.info(
          isPartner
            ? "Sent to the Pravartanam Partner Manager for approval — nothing has been changed " +
                "on the register yet."
            : `Sent to the ${data.awaiting_units.join(", ")} SPOC for approval — nothing has been ` +
                "changed on the register yet.",
        );
      if (data.kind === "coursera" && data.learners)
        toast.success(`${data.learners} learners refreshed.`);
      else if (data.kind === "template" && data.applied?.length) {
        const sum = (k) => data.applied.reduce((t, a) => t + a[k], 0);
        toast.success(
          `${sum("added")} added, ${sum("changed")} updated, ${sum("dropped")} dropped across ` +
            `${data.applied.map((a) => a.unit).join(", ")}.`,
        );
      } else if (data.kind === "team")
        toast.success(`${data.total} team member(s) brought in.`);
      else if (data.kind === "compliance")
        toast.success(
          `${data.use_cases.length} usecase(s) — ${data.readings} day(s) of compliance in.`,
        );
      else if (data.kind === "invoices")
        toast.success(
          `Invoice Tracker replaced — ${data.rows} invoice(s) across ${data.entities.length} ` +
            "entity/entities.",
        );
      else if (data.applied?.length)
        toast.success(
          `Updated ${data.applied.map((a) => a.unit).join(", ")}. ${
            data.skipped?.length ? `${data.skipped.length} skipped.` : ""
          }`,
        );
      else if (!data.awaiting_units?.length)
        toast.error(data.skipped?.[0]?.reason || "Nothing was applied.");
      if (data.kind === "coursera" || data.kind === "compliance" || data.kind === "team" ||
          data.kind === "invoices" ||
          data.applied?.length || data.awaiting_units?.length)        setStaged(null);
      load();
    } catch (err) {
      setRefusal(apiError(err));
      toast.error(apiError(err));
    } finally {
      setApplying(false);
    }
  };

  const discard = async () => {
    if (staged?.id) await api.delete(`/registers/uploads/${staged.id}`).catch(() => {});
    setStaged(null);
    setResult(null);
    toast.success("Upload discarded. Nothing was changed.");
  };

  const olderPicked = (staged?.units || []).some((u) => picked.includes(u.unit) && u.is_older);

  return (
    <div className="p-5 sm:p-8" data-testid="data-files-page">
      <div className="label-caps">Data files</div>
      <h1 className="font-display text-3xl font-black tracking-tight sm:text-4xl">
        {canUpload ? "Bring in the latest workbook" : "The unit's data files"}
      </h1>
        <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
        {!canUpload ? (
          <>
            Take out the <b>Missing Data Report</b> here — every mandatory entry your unit has left
            blank, usecase by usecase, with the officer answerable for each row. Bringing in a
            workbook is the unit SPOC's own call.
          </>
        ) : isPartnerManager ? (
          <>
            Drop the portal's own bulk update workbook — only its Pravartanam-TV rows are read — the
            compliance workbook, the Coursera tracker or the <b>McKinsey payments tracker</b>{" "}
            here. The portal shows you exactly what will change and saves nothing until you confirm.
            What you bring in is written straight away; it is the partner users' work that waits for
            you.
          </>
        ) : isPartner ? (
          <>
            Drop the portal's own bulk update workbook — only its Pravartanam-TV rows are read — the
            compliance workbook or the Coursera tracker here. The portal shows you exactly what
            will change and saves nothing until you confirm; everything you bring in then waits for
            the Pravartanam Partner Manager.
          </>
        ) : (
          <>
            Drop{" "}
            {scopedPlant
              ? `the ${scopedPlant} register`
              : "a unit register, the Coursera tracker, the compliance workbook, the McKinsey payments tracker or the digitalisation team list"}{" "}
            here. The portal reads it, shows you exactly what will change, and saves nothing until
            you confirm. For every unit the newest dated file is the one that counts.
          </>
        )}
      </p>

      {shows("data.missing") && (
        <div className="mt-6" data-testid="data-files-missing">
          <MissingDataPanel />
        </div>
      )}

      {shows("data.templates") && canUpload && (
        <>
          <TemplateDownload
            units={library?.template_units || []}
            plantNames={library?.plant_names || {}}
            myPlant={library?.my_plant}
            version={library?.template_version}
            firstFill={library?.first_fill_units || []}
          />

          <ComplianceTemplateDownload
            units={library?.template_units || []}
            plantNames={library?.plant_names || {}}
            myPlant={library?.my_plant}
          />
        </>
      )}
      {shows("data.upload") && writable("data") && canUpload && (
      <div
        data-testid="drop-zone"
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          send(e.dataTransfer.files?.[0]);
        }}
        onClick={() => fileRef.current?.click()}
        className={`mt-8 flex cursor-pointer flex-col items-center justify-center border-2 border-dashed p-12 text-center transition-colors duration-200 ${
          dragging ? "border-primary bg-muted" : "border-border bg-card hover:bg-muted"
        }`}
      >
        {busy ? (
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        ) : (
          <UploadCloud className="h-8 w-8 text-muted-foreground" />
        )}
        <div className="mt-4 font-display text-base font-bold">
          {busy ? "Reading the workbook…" : "Drag the .xlsx here, or click to choose"}
        </div>
        <p className="mt-2 max-w-md text-xs text-muted-foreground">
          {isPartnerManager ? (
            <>
              The portal's own bulk update workbook (its Pravartanam-TV rows), the
              compliance workbook, the Coursera training tracker or the McKinsey payments tracker.
              Nothing is saved until you confirm.
            </>
          ) : isPartner ? (
            <>
              The portal's own bulk update workbook (its Pravartanam-TV rows), the
              compliance workbook or the Coursera training tracker. Nothing is saved until you confirm.
            </>
          ) : (
            <>
              Unit registers in the standard Pravartanam format, or the portal's own bulk update
              workbook from above
              {isAdmin ? ", the Coursera training tracker, or the McKinsey payments tracker" : ""}.
              Nothing is saved until you confirm.
            </>
          )}
        </p>
        <input
          ref={fileRef}
          type="file"
          accept=".xlsx,.xlsm"
          hidden
          data-testid="file-input"
          onChange={(e) => send(e.target.files?.[0])}
        />
      </div>
      )}

      {shows("data.upload") && writable("data") && canUpload && (
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <span className="label-caps">Date of this file (optional)</span>
        <DayField
          className="w-[180px]"
          testId="as-on-input"
          value={asOn}
          onChange={setAsOn}
        />
        <span className="text-[11px] text-muted-foreground">
          Leave it blank and we use the "Last Updated" cell of the sheet — if that is empty too,
          today's date is filled in automatically.
        </span>
      </div>
      )}

      {refusal && (
        <div
          className="mt-4 flex items-start gap-3 border border-[hsl(var(--at-risk))] bg-card p-4"
          data-testid="upload-refused"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-[hsl(var(--at-risk))]" />
          <div className="text-sm">
            <div className="font-display text-xs font-bold uppercase tracking-widest text-[hsl(var(--at-risk))]">
              This file was not read
            </div>
            <p className="mt-1 text-muted-foreground">{refusal}</p>
          </div>
          <button
            type="button"
            data-testid="dismiss-refusal"
            onClick={() => setRefusal("")}
            className="ml-auto font-display text-[10px] font-bold uppercase tracking-widest text-muted-foreground transition-colors duration-200 hover:text-foreground"
          >
            Dismiss
          </button>
        </div>
      )}

      <UploadBlockers data={blockers} onDismiss={() => setBlockers(null)} />

      <ConfirmDialog
        open={askApply}
        onOpenChange={setAskApply}
        title="Bring this file into the register?"
        description={
          staged?.kind === "template"
            ? isPartner
              ? `${staged?.file_name} · ${picked.join(", ") || "no unit"}. Only the Pravartanam-TV rows are read, and every one of them waits for the Pravartanam Partner Manager — nothing is written to the register yet.`
              : `${staged?.file_name} · ${picked.join(", ") || "no unit"}. Whatever is not your own unit's SPOC work waits for their approval; the rest is written to the register straight away.`
            : `${staged?.file_name} will be read into the portal now.`
        }
        changes={(staged?.template?.units || [])
          .filter((u) => picked.includes(u.unit))
          .map((u) => ({
            label: u.unit,
            from: `${u.in_portal ?? 0} on record`,
            to: `${(u.added || []).length} new, ${(u.changed || []).length} updated, ${
              (u.dropped || []).length
            } dropped`,
          }))}
        confirmLabel="Yes, apply it"
        testId="apply-confirm"
        onConfirm={apply}
      />

      {library?.can_flush && (
        <>
          <FlushAll onDone={load} />
          <FlushUnit
            units={library.flush_units || []}
            plantNames={library.plant_names || {}}
            onDone={load}
          />
        </>
      )}

      {staged?.kind === "template" && (
        <TemplateDiff
          staged={staged}
          picked={picked}
          setPicked={setPicked}
          dropMissing={dropMissing}
          setDropMissing={setDropMissing}
          replaceAll={replaceAll}
          setReplaceAll={setReplaceAll}
          firstFill={staged?.first_fill_units || []}
          tvOnly={isPartner}
          applying={applying}
          onApply={() => setAskApply(true)}
          onDiscard={discard}
        />
      )}

      {staged?.kind === "register" && (
        <div className="mt-8 space-y-6" data-testid="staged-register">
          <div className="flex flex-wrap items-center justify-between gap-3 border border-border bg-card p-4">
            <div>
              <div className="font-display text-base font-bold">{staged.file_name}</div>
              <div className="text-xs text-muted-foreground">
                {staged.size_kb} KB · {staged.units.length} unit sheet(s) found · read by{" "}
                {staged.uploaded_by}
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                className="h-10 rounded-none"
                data-testid="discard-upload"
                onClick={discard}
              >
                <Trash2 className="mr-2 h-4 w-4" /> Discard
              </Button>
              <Button
                className="h-10 rounded-none font-display text-xs font-bold uppercase tracking-widest"
                disabled={applying || !picked.length || (olderPicked && !force)}
                data-testid="apply-upload"
                onClick={() => setAskApply(true)}
              >
                {applying ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Check className="mr-2 h-4 w-4" />
                )}
                Apply to {picked.length} unit(s)
              </Button>
            </div>
          </div>

          {staged.blocked?.map((b) => (
            <p
              key={b.unit}
              className="border border-border bg-card p-3 text-xs text-muted-foreground"
            >
              {b.unit} sheet ignored — {b.reason}
            </p>
          ))}

          {staged.units.map((u) => (
            <div key={u.unit} className="border border-border bg-card" data-testid={`diff-${u.unit}`}>
              <div className="flex flex-wrap items-start justify-between gap-3 border-b border-border p-4">
                <label className="flex items-start gap-3">
                  <Checkbox
                    className="mt-1"
                    checked={picked.includes(u.unit)}
                    data-testid={`pick-${u.unit}`}
                    onCheckedChange={(v) =>
                      setPicked(v ? [...picked, u.unit] : picked.filter((x) => x !== u.unit))
                    }
                  />
                  <span>
                    <span className="font-display text-lg font-bold">{u.unit}</span>
                    <span className="ml-2 text-xs text-muted-foreground">{u.unit_name}</span>
                    <div className="font-data mt-1 text-xs">
                      File dated {u.as_on}
                      {u.as_on_source === "auto" && (
                        <span className="ml-2 border border-border px-1.5 py-0.5 text-[10px] uppercase text-muted-foreground">
                          auto-filled with today's date
                        </span>
                      )}
                      {u.as_on_source === "sheet" && (
                        <span className="ml-2 text-[10px] uppercase text-muted-foreground">
                          from the sheet
                        </span>
                      )}
                      {u.as_on_source === "given" && (
                        <span className="ml-2 text-[10px] uppercase text-muted-foreground">
                          as you typed
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-muted-foreground">
                      Portal currently holds{" "}
                      {u.current ? `${u.current.as_on} (${u.current.file_name})` : "nothing"}
                    </div>
                  </span>
                </label>
                {u.is_older && (
                  <div
                    className="flex max-w-md items-start gap-2 border border-primary p-3 text-xs"
                    data-testid={`older-warning-${u.unit}`}
                  >
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                    <span>
                      This file is <b>older</b> than the {u.current?.as_on} data already in the
                      portal. Tick "apply anyway" below if you are sure.
                    </span>
                  </div>
                )}
                {u.is_same_date && (
                  <span className="text-[11px] text-muted-foreground">
                    Same date as the file already in use — this will simply refresh it.
                  </span>
                )}
              </div>

              <div className="grid grid-cols-2 gap-px bg-border sm:grid-cols-5">
                <Count label="Rows in file" value={u.diff.in_file} />
                <Count label="New" value={u.diff.added.length} tone="hsl(var(--healthy))" />
                <Count label="Updated" value={u.diff.changed.length} tone="hsl(var(--watch))" />
                <Count label="Removed" value={u.diff.removed.length} tone="hsl(var(--at-risk))" />
                <Count label="Unchanged" value={u.diff.unchanged} />
              </div>

              <div className="space-y-2 p-4">
                <Fold
                  testId={`fold-added-${u.unit}`}
                  title="New projects"
                  count={u.diff.added.length}
                >
                  {u.diff.added.map((a, i) => (
                    <Row key={i}>
                      <span className="flex items-start gap-2">
                        <Plus className="mt-0.5 h-3 w-3 shrink-0 text-[hsl(var(--healthy))]" />
                        <span>
                          <b>{a.title}</b>
                          <span className="ml-2 text-muted-foreground">{a.stage}</span>
                        </span>
                      </span>
                    </Row>
                  ))}
                </Fold>
                <Fold
                  testId={`fold-changed-${u.unit}`}
                  title="Changed projects"
                  count={u.diff.changed.length}
                >
                  {u.diff.changed.map((c) => (
                    <Row key={c.code}>
                      <div className="font-data text-[11px] text-muted-foreground">{c.code}</div>
                      <div className="font-semibold">{c.title}</div>
                      <ul className="mt-1 space-y-0.5">
                        {c.fields.map((f) => (
                          <li key={f.field} className="text-[11px]">
                            <span className="text-muted-foreground">{f.label}: </span>
                            <span className="line-through decoration-[hsl(var(--at-risk))]">
                              {String(f.from ?? "—")}
                            </span>
                            <span className="mx-1">→</span>
                            <b>{String(f.to ?? "—")}</b>
                          </li>
                        ))}
                      </ul>
                    </Row>
                  ))}
                </Fold>
                <Fold
                  testId={`fold-removed-${u.unit}`}
                  title="Projects no longer in the file"
                  count={u.diff.removed.length}
                >
                  {u.diff.removed.map((r) => (
                    <Row key={r.code}>
                      <span className="flex items-start gap-2">
                        <Minus className="mt-0.5 h-3 w-3 shrink-0 text-[hsl(var(--at-risk))]" />
                        <span>
                          <span className="font-data text-[11px] text-muted-foreground">
                            {r.code}
                          </span>{" "}
                          <b>{r.title}</b> — will be removed from the portal
                        </span>
                      </span>
                    </Row>
                  ))}
                </Fold>
              </div>
            </div>
          ))}

          {olderPicked && (
            <label className="flex items-center gap-3 border border-primary bg-card p-4 text-sm">
              <Checkbox
                checked={force}
                data-testid="force-older"
                onCheckedChange={(v) => setForce(!!v)}
              />
              Yes, apply this older file anyway — I know it will replace newer data.
            </label>
          )}
        </div>
      )}

      {staged?.kind === "coursera" && (
        <div className="mt-8 border border-border bg-card" data-testid="staged-coursera">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border p-4">
            <div>
              <div className="font-display text-base font-bold">{staged.file_name}</div>
              <div className="text-xs text-muted-foreground">
                Coursera training tracker · sheet "{staged.sheet_used}" · {staged.size_kb} KB
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                className="h-10 rounded-none"
                data-testid="discard-upload"
                onClick={discard}
              >
                <Trash2 className="mr-2 h-4 w-4" /> Discard
              </Button>
              <Button
                className="h-10 rounded-none font-display text-xs font-bold uppercase tracking-widest"
                disabled={applying}
                data-testid="apply-upload"
                onClick={() => setAskApply(true)}
              >
                {applying ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Check className="mr-2 h-4 w-4" />
                )}
                Refresh training data
              </Button>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-px bg-border sm:grid-cols-5">
            <Count label="Learners in file" value={staged.coursera.in_file} />
            <Count label="New" value={staged.coursera.added.length} tone="hsl(var(--healthy))" />
            <Count label="Updated" value={staged.coursera.changed.length} tone="hsl(var(--watch))" />
            <Count label="Dropped" value={staged.coursera.removed.length} tone="hsl(var(--at-risk))" />
            <Count label="Unchanged" value={staged.coursera.unchanged} />
          </div>
          <div className="space-y-2 p-4">
            <Fold testId="fold-added-coursera" title="New learners" count={staged.coursera.added.length}>
              {staged.coursera.added.map((a) => (
                <Row key={a.email}>
                  <b>{a.name}</b>{" "}
                  <span className="font-data text-muted-foreground">{a.email}</span> · {a.plant}
                </Row>
              ))}
            </Fold>
            <Fold
              testId="fold-changed-coursera"
              title="Progress changed"
              count={staged.coursera.changed.length}
            >
              {staged.coursera.changed.map((c) => (
                <Row key={c.email}>
                  <div className="font-semibold">{c.name}</div>
                  <ul className="mt-1 space-y-0.5">
                    {c.fields.map((f) => (
                      <li key={f.field} className="text-[11px]">
                        <span className="text-muted-foreground">{f.label}: </span>
                        {String(f.from ?? "—")} → <b>{String(f.to ?? "—")}</b>
                      </li>
                    ))}
                  </ul>
                </Row>
              ))}
            </Fold>
            <Fold
              testId="fold-removed-coursera"
              title="No longer in the tracker"
              count={staged.coursera.removed.length}
            >
              {staged.coursera.removed.map((r) => (
                <Row key={r.email}>
                  <b>{r.name || "—"}</b>{" "}
                  <span className="font-data text-muted-foreground">{r.email}</span>
                </Row>
              ))}
            </Fold>
          </div>
        </div>
      )}

      {staged?.kind === "compliance" && (
        <div className="mt-8 border border-border bg-card" data-testid="staged-compliance">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border p-4">
            <div>
              <div className="font-display text-base font-bold">{staged.file_name}</div>
              <div className="text-xs text-muted-foreground">
                Compliance workbook · {staged.compliance.rows} row(s) read from{" "}
                {(staged.compliance.sheets || []).join(", ") || "its unit sheets"} · nothing is
                saved yet
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                className="h-10 rounded-none"
                data-testid="discard-upload"
                onClick={discard}
              >
                <Trash2 className="mr-2 h-4 w-4" /> Discard
              </Button>
              <Button
                className="h-10 rounded-none font-display text-xs font-bold uppercase tracking-widest"
                disabled={applying}
                data-testid="apply-upload"
                onClick={() => setAskApply(true)}
              >
                {applying ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Check className="mr-2 h-4 w-4" />
                )}
                Bring in the compliance data
              </Button>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-px bg-border sm:grid-cols-6">
            <Count label="Usecases in file" value={staged.compliance.use_cases.length} />
            <Count
              label="New days"
              value={staged.compliance.readings_new}
              tone="hsl(var(--healthy))"
            />
            <Count
              label="Corrected days"
              value={staged.compliance.readings_changed}
              tone="hsl(var(--watch))"
            />
            <Count
              label="Days to be removed"
              value={staged.compliance.readings_removed || 0}
              tone={
                staged.compliance.readings_removed ? "hsl(var(--at-risk))" : undefined
              }
            />
            <Count label="Unchanged" value={staged.compliance.readings_unchanged} />
            <Count
              label="Refused rows"
              value={(staged.compliance.refused || []).length}
              tone={
                (staged.compliance.refused || []).length ? "hsl(var(--at-risk))" : undefined
              }
            />
          </div>
          <div className="space-y-3 p-4">
            {staged.compliance.use_cases.map((u) => (
              <div
                key={u.project_id}
                className="border border-border p-3"
                data-testid={`compliance-diff-${u.code}`}
              >
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <div>
                    <span className="font-data text-[11px] text-muted-foreground">
                      {u.plant} · {u.code}
                    </span>
                    <div className="font-display text-sm font-bold">{u.title}</div>
                  </div>
                  <span className="font-data text-xs">
                    {u.tracked ? "already on watch" : "will go on compliance watch"}
                  </span>
                </div>
                <div className="mt-2 grid gap-1 text-[11px] text-muted-foreground sm:grid-cols-2">
                  <span>
                    {u.rows} row(s) in the file · {u.new_days.length} new day(s) ·{" "}
                    {u.changed_days.length} corrected · {u.unchanged_days} unchanged
                  </span>
                  <span>
                    {u.profile_changes.length
                      ? u.profile_changes
                          .map(
                            (c) =>
                              `${c.label}: ${c.from === null || c.from === undefined ? "not declared" : c.from} → ${c.to === null || c.to === undefined ? "cleared" : c.to}`,
                          )
                          .join(" · ")
                      : "the settled-once figures are unchanged"}
                  </span>
                </div>
                {(u.removed_days || []).length > 0 && (
                  <p
                    className="mt-2 text-[11px] font-semibold text-[hsl(var(--at-risk))]"
                    data-testid={`compliance-removed-${u.code}`}
                  >
                    {u.removed_days.length} day(s) will be taken off the record —{" "}
                    {u.removed_days
                      .slice(0, 6)
                      .map((d) => d.date)
                      .join(", ")}
                    {u.removed_days.length > 6 ? " …" : ""}. Every figure of those days was
                    written NA.
                  </p>
                )}
                {u.needs_kpi && (
                  <p className="mt-2 text-[11px] font-semibold text-[hsl(var(--at-risk))]">
                    Name the KPI in KPI_Name before this usecase can go on watch — it will be
                    refused otherwise.
                  </p>
                )}
              </div>
            ))}
            {(staged.compliance.refused || []).length > 0 && (
              <div
                className="border border-[hsl(var(--at-risk))] p-3"
                data-testid="compliance-refused"
              >
                <div className="label-caps text-[hsl(var(--at-risk))]">
                  These rows will be left out, and why
                </div>
                <ul className="mt-2 space-y-1 text-[11px] text-muted-foreground">
                  {staged.compliance.refused.slice(0, 40).map((r, i) => (
                    <li key={`${r.code}-${r.row}-${i}`}>
                      <b className="text-foreground">{r.code || "no code"}</b>
                      {r.sheet ? ` · ${r.sheet} row ${r.row}` : ""} — {r.reason}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}

      {staged?.kind === "team" && (
        <div className="mt-8 border border-border bg-card" data-testid="staged-team">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border p-4">
            <div>
              <div className="font-display text-base font-bold">{staged.file_name}</div>
              <div className="text-xs text-muted-foreground">
                Digitalisation team list · {staged.team.total} member(s) ·{" "}
                {Object.entries(staged.team.bands)
                  .map(([b, n]) => `${b} ${n}`)
                  .join(" · ")}
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                className="h-10 rounded-none"
                data-testid="discard-upload"
                onClick={discard}
              >
                <Trash2 className="mr-2 h-4 w-4" /> Discard
              </Button>
              <Button
                className="h-10 rounded-none font-display text-xs font-bold uppercase tracking-widest"
                disabled={applying}
                data-testid="apply-upload"
                onClick={() => setAskApply(true)}
              >
                {applying ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Check className="mr-2 h-4 w-4" />
                )}
                Update the team
              </Button>
            </div>
          </div>
          <div className="space-y-3 p-4">
            {staged.team.units.map((u) => (
              <div
                key={u.unit}
                className="border border-border p-3"
                data-testid={`team-diff-${u.unit}`}
              >
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <span className="font-display text-sm font-bold">{u.unit}</span>
                  <span className="font-data text-[11px] text-muted-foreground">
                    {u.blocked
                      ? "not your unit — this sheet will be left alone"
                      : `${u.members} in file · ${u.added.length} new · ${u.changed.length} changed · ${u.unchanged} unchanged · ${u.missing.length} to be removed`}
                  </span>
                </div>
                {!u.blocked && (u.added.length > 0 || u.changed.length > 0 || u.missing.length > 0) && (
                  <ul className="mt-2 space-y-0.5 text-[11px] text-muted-foreground">
                    {u.added.slice(0, 6).map((a) => (
                      <li key={a.name}>
                        <span className="text-[hsl(var(--healthy))]">new</span> {a.name} — {a.band}
                      </li>
                    ))}
                    {u.changed.slice(0, 6).map((c) => (
                      <li key={c.name}>
                        <span className="text-[hsl(var(--watch))]">changed</span> {c.name} —{" "}
                        {c.fields.map((f) => `${f.label}: ${f.from || "blank"} → ${f.to || "blank"}`).join("; ")}
                      </li>
                    ))}
                    {u.missing.length > 0 && (
                      <li>
                        <span className="text-destructive">removing</span>{" "}
                        {u.missing.length} member(s) held in the portal but not in this file —
                        this sheet is the whole {u.unit} team: {u.missing.slice(0, 5).join(", ")}
                        {u.missing.length > 5 ? "…" : ""}
                      </li>
                    )}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {staged?.kind === "invoices" && (
        <div className="mt-8 border border-border bg-card" data-testid="staged-invoices">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border p-4">
            <div>
              <div className="font-display text-base font-bold">{staged.file_name}</div>
              <div className="text-xs text-muted-foreground">
                McKinsey payments tracker · {staged.invoices.rows} invoice(s) ·{" "}
                {staged.invoices.entities.join(", ")}
                {staged.invoices.roadmap_rows
                  ? ` · payments roadmap ${staged.invoices.roadmap_rows} row(s)`
                  : ""}
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                className="h-10 rounded-none"
                data-testid="discard-upload"
                onClick={discard}
              >
                <Trash2 className="mr-2 h-4 w-4" /> Discard
              </Button>
              <Button
                className="h-10 rounded-none font-display text-xs font-bold uppercase tracking-widest"
                disabled={applying}
                data-testid="apply-upload"
                onClick={() => setAskApply(true)}
              >
                {applying ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Check className="mr-2 h-4 w-4" />
                )}
                Replace the Invoice Tracker
              </Button>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-px bg-border sm:grid-cols-4">
            <Count label="Invoices in file" value={staged.invoices.totals.invoices} />
            <Count label="Total invoiced" value={rupees(staged.invoices.totals.invoiced)} />
            <Count
              label="Payment cleared"
              value={rupees(staged.invoices.totals.cleared_amount)}
              tone="hsl(var(--healthy))"
            />
            <Count
              label="Payment pending"
              value={rupees(staged.invoices.totals.pending_amount)}
              tone="hsl(var(--watch))"
            />
          </div>
          <div className="space-y-3 p-4">
            <p className="border-l-2 border-[hsl(var(--watch))] pl-3 text-xs">
              This file <b>replaces the whole Invoice Tracker</b>.{" "}
              {staged.invoices.replacing.rows
                ? `What the portal holds now — ${staged.invoices.replacing.rows} invoice(s) from
                   ${staged.invoices.replacing.file_name}, brought in by
                   ${staged.invoices.replacing.uploaded_by} — is kept on record and can be taken out
                   again from the Invoice Tracker tab.`
                : "The portal holds no invoice data yet, so this will be the first."}
            </p>
            {staged.invoices.mismatches?.length > 0 && (
              <div className="border border-[hsl(var(--watch))] p-3" data-testid="invoice-mismatch">
                <div className="flex items-center gap-2 text-xs font-semibold text-[hsl(var(--watch))]">
                  <AlertTriangle className="h-3.5 w-3.5" /> The file's Summary sheet does not match
                  its own rows
                </div>
                <ul className="mt-1.5 space-y-1 text-[11px] text-muted-foreground">
                  {staged.invoices.mismatches.map((m) => (
                    <li key={m}>• {m}</li>
                  ))}
                </ul>
                <p className="mt-1.5 text-[11px] text-muted-foreground">
                  The portal will add up the invoice rows themselves, so the tab always ties with
                  the detail.
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {result && (
        <div className="mt-8 border border-border bg-card p-5" data-testid="apply-result">
          <h2 className="font-display text-lg font-bold">
            {result.awaiting_units?.length && !result.applied?.length
              ? "What is waiting for approval"
              : "What was saved"}
          </h2>
          {result.awaiting_units?.length > 0 && (
            <p className="mt-2 border-l-2 border-[hsl(var(--watch))] pl-3 text-sm">
              {isPartner
                ? `Nothing has been written to the register. Every ${result.awaiting_units.join(", ")} row in this file is now with the Pravartanam Partner Manager, and appears on your Approvals screen until he clears it.`
                : `${result.awaiting_units.join(", ")} is waiting for that unit's SPOC to approve it — nothing has been written to the register yet.`}
            </p>
          )}
          {result.awaiting_units?.length ? null : result.kind === "coursera" ? (
            <p className="mt-2 text-sm">
              {result.learners} learners refreshed from sheet "{result.sheet_used}".
            </p>
          ) : result.kind === "team" ? (
            <ul className="mt-2 space-y-1 text-sm">
              {result.applied?.map((u) => (
                <li key={u.unit}>
                  <b>{u.unit}</b> — {u.members} member(s): {u.added} added, {u.updated} updated,{" "}
                  {u.removed || 0} removed
                  {u.removed_names?.length
                    ? ` (${u.removed_names.slice(0, 6).join(", ")}${
                        u.removed_names.length > 6 ? "…" : ""
                      })`
                    : ""}
                </li>
              ))}
              {result.skipped?.map((s) => (
                <li key={s.unit} className="text-muted-foreground">
                  {s.unit} skipped — {s.reason}
                </li>
              ))}
            </ul>
          ) : result.kind === "compliance" ? (
            <ul className="mt-2 space-y-1 text-sm">
              {result.use_cases?.map((u) => (
                <li key={u.code}>
                  <b>{u.code}</b> ({u.plant}) — {u.readings} day(s) in, {u.new_days} new,{" "}
                  {u.changed_days} corrected
                  {u.removed_days ? `, ${u.removed_days} taken off` : ""}
                  {u.newly_tracked ? " · now on compliance watch" : ""}
                  {u.declared?.length ? ` · declared ${u.declared.join(", ")}` : ""}
                </li>
              ))}
              <li className="text-muted-foreground">
                {result.readings} day(s) of compliance written · data as on {result.as_on}
                {result.readings_removed ? ` · ${result.readings_removed} day(s) removed` : ""}
                {result.profiles_made ? ` · ${result.profiles_made} put on watch` : ""}
              </li>
              {result.refused?.map((r, i) => (
                <li key={`${r.code}-${i}`} className="text-muted-foreground">
                  {r.code || "no code"} left out — {r.reason}
                </li>
              ))}
            </ul>
          ) : result.kind === "invoices" ? (
            <ul className="mt-2 space-y-1 text-sm">
              <li>
                <b>{result.rows}</b> invoice(s) across {result.entities?.join(", ")} — total invoiced{" "}
                {rupees(result.totals?.invoiced)}, cleared {rupees(result.totals?.cleared_amount)},
                pending {rupees(result.totals?.pending_amount)}.
              </li>
              <li className="text-muted-foreground">
                The Invoice Tracker tab now reads from this file. The one it replaced is still on
                record there.
              </li>
            </ul>
          ) : (
            <ul className="mt-2 space-y-1 text-sm">
              {result.applied?.map((a) => (
                <li key={a.unit}>
                  <b>{a.unit}</b> — {a.total} projects as on {a.as_on} ({a.added} new, {a.changed}{" "}
                  updated, {a.removed} removed)
                </li>
              ))}
              {result.skipped?.map((s) => (
                <li key={s.unit} className="text-muted-foreground">
                  <b>{s.unit}</b> skipped — {s.reason}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="mt-10">
        <h2 className="font-display text-xl font-bold tracking-tight">
          What the portal is using now
        </h2>
        <div className="mt-4 grid gap-px bg-border sm:grid-cols-2 lg:grid-cols-4">
          {Object.entries(library?.active || {}).map(([unit, src]) => (
            <div key={unit} className="bg-card p-4" data-testid={`active-${unit}`}>
              <div className="flex items-baseline justify-between">
                <span className="font-display text-base font-bold">{unit}</span>
                <span className="font-data text-xs">{src?.as_on || "—"}</span>
              </div>
              <div className="mt-1 truncate text-[11px] text-muted-foreground">
                {src?.file_name || "No register held yet"}
              </div>
              <div className="text-[11px] text-muted-foreground">
                {src?.rows ?? 0} rows · by {src?.by || "—"}
              </div>
            </div>
          ))}
        </div>

        {shows("data.history") && (
        <div className="mt-6 overflow-x-auto border border-border bg-card">
          <table className="w-full min-w-[760px] text-sm" data-testid="library-table">
            <thead className="bg-muted">
              <tr className="text-left">
                {["File", "Type", "Units and dates", "Brought in by", "When", ""].map((h) => (
                  <th key={h} className="label-caps px-4 py-3">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {library?.files?.map((f) => (
                <tr key={f.id}>
                  <td className="px-4 py-3">
                    <span className="flex items-center gap-2">
                      <FileSpreadsheet className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">{f.file_name}</span>
                    </span>
                    <span className="text-[11px] text-muted-foreground">{f.size_kb} KB</span>
                  </td>
                  <td className="px-4 py-3 text-xs">
                    {f.kind === "coursera"
                      ? "Coursera tracker"
                      : f.kind === "compliance"
                        ? "Compliance dashboard"
                        : f.kind === "invoices"
                          ? "McKinsey payments tracker"
                          : f.kind === "team"
                            ? "Digitalisation team"
                            : "Unit register"}
                    <div className="text-[11px] text-muted-foreground">
                      {f.origin === "seed" ? "shipped with the portal" : "uploaded"}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1.5">
                      {f.units?.map((u) => {
                        const active = library.active?.[u.unit]?.file_name === f.file_name;
                        return (
                          <span
                            key={u.unit}
                            className={`border px-2 py-0.5 text-[11px] ${
                              active
                                ? "border-primary text-primary"
                                : "border-border text-muted-foreground"
                            }`}
                          >
                            {u.unit} · {u.as_on}
                            {active ? " · in use" : ""}
                          </span>
                        );
                      })}
                      {!f.units?.length && (
                        <span className="text-[11px] text-muted-foreground">
                          Whole-SAIL training data
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs">{f.uploaded_by}</td>
                  <td className="px-4 py-3 text-xs">{fmtWhen(f.uploaded_at)}</td>
                  <td className="px-4 py-3 text-right">
                    <Button
                      variant="outline"
                      size="sm"
                      data-testid={`library-download-${f.id}`}
                      onClick={() =>
                        downloadFile(`/registers/library/${f.id}/download`, {}, f.file_name)
                          .then(() => toast.success(`${f.file_name} is on its way down.`))
                          .catch((e) => toast.error(apiError(e)))
                      }
                    >
                      <Download className="mr-2 h-3.5 w-3.5" /> Take it back out
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}

        {library?.coursera?.file_name && (
          <p className="font-data mt-4 text-[11px] text-muted-foreground">
            Training data in use: {library.coursera.file_name} · {library.coursera.learners} learners
            · brought in by {library.coursera.uploaded_by} on {fmtWhen(library.coursera.uploaded_at)}
          </p>
        )}
      </div>
    </div>
  );
}
